import React, { useState } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import {
  Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { Icon, T } from '../components/PartnerSignups/onboardingDesign'
import {
  Badge, Card, FieldLabel, GhostButton, LinkButton, Notice, OnboardingPage, OutlineButton, PageHeader, PrimaryButton, StatCard,
} from '../components/PartnerSignups/onboardingUi'

/**
 * Super admin: restaurants already on RushMyFood and where each is with the
 * merchant agreement (backend utils/currentMerchants.js). Send agreement
 * starts an application attached to the restaurant, filled in with its
 * details, and emails the owner to complete and sign it. Approving it keeps
 * the restaurant and its login; it adds the agreement, the signed commission
 * and the payment setup (Stripe and QorCommerce, where missing).
 */

const CURRENT_MERCHANTS = gql`
  query CurrentMerchants {
    currentMerchants {
      restaurantId name address isActive ownerName ownerEmail ownerPhone hasStripe hasQor
      applicationId applicationStatus applicationSentAt signedAt
    }
  }
`
const SEND_AGREEMENT = gql`
  mutation SendMerchantAgreement($restaurantId: ID!, $input: MerchantAgreementInput) {
    sendMerchantAgreement(restaurantId: $restaurantId, input: $input) { applicationId emailed }
  }
`

const APP_PAGE = '/super_admin/partner-application'

// Where a restaurant is with the agreement.
function agreementOf(m) {
  if (!m.applicationId || m.applicationStatus === 'REJECTED') return { key: 'NONE', label: m.applicationStatus === 'REJECTED' ? 'Rejected' : 'Not sent', kind: m.applicationStatus === 'REJECTED' ? 'red' : 'neutral' }
  if (m.applicationStatus === 'ONBOARDED') return { key: 'DONE', label: 'Done', kind: 'green' }
  if (m.applicationStatus === 'SIGNED') return { key: 'SIGNED', label: 'Signed, approve it', kind: 'purple' }
  return { key: 'SENT', label: m.applicationSentAt ? `Sent ${new Date(m.applicationSentAt).toLocaleDateString()}` : 'Started, not sent', kind: m.applicationSentAt ? 'info' : 'amber' }
}

const headCell = { fontWeight: 600, fontSize: 13, color: T.muted, whiteSpace: 'nowrap', bgcolor: T.footerBg }
const tableSx = {
  '& .MuiTableCell-root': { px: 2 },
  '& .MuiTableCell-body': { py: 1.5, verticalAlign: 'top' },
  '& .MuiTableCell-root:first-of-type': { pl: { xs: 2, sm: 3 } },
  '& .MuiTableCell-root:last-of-type': { pr: { xs: 2, sm: 3 } },
  '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': { borderBottom: 0 },
}

function SendDialog({ merchant, onClose, onDone }) {
  const ownerName = String(merchant.ownerName || '').trim().split(/\s+/)
  const [f, setF] = useState({
    firstName: ownerName[0] || '',
    lastName: ownerName.slice(1).join(' '),
    email: merchant.ownerEmail || '',
    phone: merchant.ownerPhone || '',
  })
  const [send, { loading }] = useMutation(SEND_AGREEMENT)
  const [error, setError] = useState(null)
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }))
  const go = async (emailNow) => {
    setError(null)
    try {
      const { data } = await send({ variables: { restaurantId: merchant.restaurantId, input: { ...f, send: emailNow } } })
      onDone(data.sendMerchantAgreement, emailNow)
    } catch (e) {
      setError(e.message)
    }
  }
  return (
    <Dialog open onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send the agreement to {merchant.name}</DialogTitle>
      <DialogContent dividers sx={{ borderColor: T.divider }}>
        {error && <Notice severity="error">{error}</Notice>}
        <Typography sx={{ color: T.text2, fontSize: 14, mb: 2, lineHeight: 1.6 }}>
          We start their application with this restaurant’s name, phone and address filled in, and email the owner a link to complete it and sign.
          When you approve it, the restaurant and its login stay as they are: only the agreement, the commission they sign and their payment setup
          ({merchant.hasStripe ? 'their Stripe account is kept' : 'a Stripe account'}, {merchant.hasQor ? 'already boarded with QorCommerce' : 'QorCommerce boarding'}) are added.
        </Typography>
        <Grid container columnSpacing={2} rowSpacing={1.75}>
          <Grid item xs={12} sm={6}>
            <FieldLabel htmlFor="owner-first" label="Owner first name" />
            <TextField id="owner-first" fullWidth size="small" value={f.firstName} onChange={set('firstName')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel htmlFor="owner-last" label="Owner last name" />
            <TextField id="owner-last" fullWidth size="small" value={f.lastName} onChange={set('lastName')} />
          </Grid>
          <Grid item xs={12} sm={7}>
            <FieldLabel htmlFor="owner-email" label="Owner email" />
            <TextField id="owner-email" fullWidth size="small" type="email" value={f.email} onChange={set('email')} helperText="The link to complete and sign goes here." />
          </Grid>
          <Grid item xs={12} sm={5}>
            <FieldLabel htmlFor="owner-phone" label="Phone" />
            <TextField id="owner-phone" fullWidth size="small" type="tel" value={f.phone} onChange={set('phone')} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        <GhostButton onClick={onClose} disabled={loading}>Cancel</GhostButton>
        <Box sx={{ flex: 1 }} />
        <OutlineButton disabled={loading || !f.email || !f.firstName} onClick={() => go(false)}>
          Fill it in first
        </OutlineButton>
        <PrimaryButton busy={loading} disabled={!f.email || !f.firstName} onClick={() => go(true)}>
          <Icon.Send size={16} />
          Email the owner
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  )
}

export default function CurrentMerchants() {
  const history = useHistory()
  const { data, loading, error, refetch } = useQuery(CURRENT_MERCHANTS, { fetchPolicy: 'network-only' })
  const [search, setSearch] = useState('')
  const [sending, setSending] = useState(null)
  const [notice, setNotice] = useState(null)
  const merchants = (data && data.currentMerchants) || []
  const q = search.trim().toLowerCase()
  const shown = q
    ? merchants.filter(m => [m.name, m.address, m.ownerName, m.ownerEmail].some(v => v && v.toLowerCase().includes(q)))
    : merchants
  const count = key => merchants.filter(m => agreementOf(m).key === key).length

  return (
    <OnboardingPage>
      <PageHeader
        title="Current merchants"
        subtitle="Restaurants already on RushMyFood. Send each owner the merchant agreement; approving it keeps their restaurant and login and adds the agreement, the signed commission and their payment setup."
        actions={(
          <GhostButton onClick={() => refetch()}>
            <Icon.Refresh size={16} />
            Reload
          </GhostButton>
        )}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 2, mb: 3 }}>
        <StatCard value={merchants.length} label="Restaurants" />
        <StatCard value={count('NONE')} label="Not sent yet" tone="red" />
        <StatCard value={count('SENT')} label="Sent, not signed" tone="info" />
        <StatCard value={count('SIGNED')} label="Signed, to approve" tone="purple" />
        <StatCard value={count('DONE')} label="Done" tone="green" />
      </Box>

      {notice && <Notice severity="success" onClose={() => setNotice(null)}>{notice}</Notice>}
      {error && <Notice severity="error">{error.message}</Notice>}

      <Card sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ p: { xs: 2, sm: 2.5 }, borderBottom: `1px solid ${T.divider}` }}>
          <TextField
            placeholder="Search by restaurant, address or owner"
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="small"
            inputProps={{ 'aria-label': 'Search current merchants' }}
            sx={{ width: { xs: '100%', sm: 420 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Box sx={{ color: T.placeholder, display: 'flex' }}><Icon.Search size={18} /></Box></InputAdornment> }}
          />
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          {loading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: T.red }} /></Box>
          ) : shown.length === 0 ? (
            <Typography sx={{ p: 5, textAlign: 'center', color: T.hint }}>{q ? 'No restaurant matches that search.' : 'No restaurants yet.'}</Typography>
          ) : (
            <Table size="small" sx={{ minWidth: 960, ...tableSx }}>
              <TableHead>
                <TableRow>
                  {['Restaurant', 'Owner', 'Payments', 'Agreement', ''].map(h => <TableCell key={h} sx={headCell}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map(m => {
                  const a = agreementOf(m)
                  return (
                    <TableRow key={m.restaurantId}>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 14.5, color: T.text }}>
                          {m.name}
                          {!m.isActive && <Badge kind="neutral" sx={{ ml: 1, fontSize: 11.5 }}>Inactive</Badge>}
                        </Typography>
                        <Typography sx={{ color: T.hint, fontSize: 13 }}>{m.address || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 230 }}>
                        <Typography sx={{ fontSize: 14, color: T.text }}>{m.ownerName || '—'}</Typography>
                        <Typography sx={{ color: T.hint, fontSize: 13, overflowWrap: 'anywhere' }}>{m.ownerEmail || 'No owner login'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                          <Badge kind={m.hasStripe ? 'green' : 'neutral'}>{m.hasStripe ? 'Stripe' : 'No Stripe'}</Badge>
                          <Badge kind={m.hasQor ? 'green' : 'neutral'}>{m.hasQor ? 'QorCommerce' : 'No QorCommerce'}</Badge>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Badge kind={a.kind}>{a.label}</Badge>
                        {m.signedAt && <Typography sx={{ color: T.hint, fontSize: 12.5, mt: 0.5 }}>Signed {new Date(m.signedAt).toLocaleDateString()}</Typography>}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {a.key === 'NONE' ? (
                          <OutlineButton onClick={() => setSending(m)} sx={{ minHeight: 36 }}>
                            <Icon.Send size={15} />
                            Send agreement
                          </OutlineButton>
                        ) : (
                          <LinkButton onClick={() => history.push(`${APP_PAGE}/${m.applicationId}`)}>
                            Open
                            <Icon.ArrowRight size={15} />
                          </LinkButton>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </Box>
      </Card>

      {sending && (
        <SendDialog
          merchant={sending}
          onClose={() => setSending(null)}
          onDone={(res, emailed) => {
            const name = sending.name
            setSending(null)
            if (emailed) {
              setNotice(`${name}: the owner was emailed the link to complete and sign the agreement.`)
              refetch()
            } else {
              history.push(`${APP_PAGE}/${res.applicationId}`)
            }
          }}
        />
      )}
    </OnboardingPage>
  )
}
